import React, { useEffect, useRef } from 'react';
import {
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  type LexicalEditor,
  type RangeSelection
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

export interface FormulaEditorLexicalHandle {
  insertTextAtCursor: (text: string) => void;
}

export const insertTextAtCursor = (
  editor: LexicalEditor,
  text: string,
  lastSelection: RangeSelection | null
): void => {
  editor.update(
    () => {
      let selection = $getSelection();

      if (
        !$isRangeSelection(selection) &&
        lastSelection &&
        $getNodeByKey(lastSelection.anchor.key) &&
        $getNodeByKey(lastSelection.focus.key)
      ) {
        selection = lastSelection.clone();
        $setSelection(selection);
      }

      if (!$isRangeSelection(selection)) {
        selection = $getRoot().selectEnd();
      }

      selection.insertText(text);
    },
    { discrete: true }
  );
};

interface ActionsBridgePluginProps {
  actionsRef: React.MutableRefObject<FormulaEditorLexicalHandle | null>;
}

export const ActionsBridgePlugin = ({ actionsRef }: ActionsBridgePluginProps) => {
  const [editor] = useLexicalComposerContext();
  const lastSelectionRef = useRef<RangeSelection | null>(null);

  useEffect(() => {
    const unregisterUpdateListener = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          lastSelectionRef.current = selection.clone();
        }
      });
    });

    actionsRef.current = {
      insertTextAtCursor: (text: string) => {
        insertTextAtCursor(editor, text, lastSelectionRef.current);
        editor.focus();
      }
    };

    return () => {
      actionsRef.current = null;
      unregisterUpdateListener();
    };
  }, [actionsRef, editor]);

  return null;
};
